import { useMemo } from 'react';
import { hasUnsavedTripChanges } from '@koob/utils';
import useTripStore from '../store/useTripStore';
import { useDays, useTripFilters } from './useTripStoreSelectors';

/**
 * Whether the composer holds changes that saving as draft would store
 * (KD-3340), shared by both apps' save buttons and Exit warnings (KD-3656).
 * `trip`/`days`/`tripFilters` all come from the shared store.
 */
export function useHasUnsavedChanges() {
  const trip = useTripStore((state) => state.trip);
  const days = useDays();
  const tripFilters = useTripFilters();

  return useMemo(
    () =>
      hasUnsavedTripChanges({
        trip,
        days,
        storedSettings: {
          startDate: trip?.startDate,
          duration: trip?.duration,
          adultPax: trip?.adultPax,
          childrenBirthdates: trip?.childrenBirthdates,
          locale: trip?.locale,
          brief: trip?.tripFilters?.brief,
          themes: trip?.tripFilters?.themes,
          styles: trip?.tripFilters?.styles,
        },
        composedSettings: {
          startDate: tripFilters?.departureDate,
          duration: tripFilters?.tripDuration,
          adultPax: tripFilters?.composition?.adults,
          childrenBirthdates: tripFilters?.composition?.childrenBirthdates,
          locale: tripFilters?.locale,
          brief: tripFilters?.brief,
          themes: tripFilters?.themes,
          styles: tripFilters?.styles,
        },
      }),
    [trip, days, tripFilters],
  );
}

/**
 * Whether Exit should ask first (KD-3656). A trip that was never saved has no
 * folder and always counts as changed, so an untouched blank composer would
 * warn too: only ask once something was actually placed on the days.
 */
export function useShouldWarnBeforeExit() {
  const hasUnsavedChanges = useHasUnsavedChanges();
  const trip = useTripStore((state) => state.trip);
  const days = useDays();

  return (
    hasUnsavedChanges &&
    (Boolean(trip?.clientFolder?.id) ||
      days.some((day: any) => day?.experienceId || day?.hotelId))
  );
}
